import type * as React from "react"

import { Rivets } from "@/components/rivets"
import { cn } from "@/lib/utils"

/* ── Neon frame gradient used on the top edge lines ──────────────────────── */
const topLineGradient =
  "linear-gradient(90deg, transparent 0%, oklch(0.8 0.3 145) 40%, oklch(0.8 0.3 145) 60%, transparent 100%) center / 30% 100% no-repeat, oklch(0.85 0.28 85)"

/**
 * Renders the doubled miter-line neon frame that surrounds every Card.
 * Each edge has two parallel lines (outer dimmer, inner brighter).
 * The top edge lines additionally carry a sweep animation on hover.
 */
function NeonFrame() {
  return (
    <div className="pointer-events-none absolute -inset-2.5" aria-hidden="true">
      {/* ── Top edge ────────────────────────────────────────────────────── */}
      <div className="absolute top-0 right-0 left-0 h-[5px]">
        {/* outer line */}
        <span
          className="group-hover:animate-neon-sweep pointer-events-none absolute top-0 right-5 left-5 h-px opacity-50"
          style={{
            background: topLineGradient,
            backgroundPosition: "50% 0, 0 0",
          }}
        />
        {/* inner line */}
        <span
          className="group-hover:animate-neon-sweep pointer-events-none absolute right-3.5 bottom-0 left-3.5 h-px"
          style={{
            background: topLineGradient,
            backgroundPosition: "50% 0, 0 0",
          }}
        />
      </div>

      {/* ── Left edge ───────────────────────────────────────────────────── */}
      <div className="absolute top-0 bottom-0 left-0 w-[5px]">
        {/* outer line */}
        <span
          className="pointer-events-none absolute top-5 bottom-5 left-0 w-px"
          style={{ background: "oklch(0.8 0.28 85 / 0.5)" }}
        />
        {/* inner line */}
        <span
          className="pointer-events-none absolute top-3.5 right-0 bottom-3.5 w-px"
          style={{ background: "oklch(0.8 0.28 85)" }}
        />
      </div>

      {/* ── Right edge ──────────────────────────────────────────────────── */}
      <div className="absolute top-0 right-0 bottom-0 w-[5px]">
        {/* inner line */}
        <span
          className="pointer-events-none absolute top-3.5 bottom-3.5 left-0 w-px"
          style={{ background: "oklch(0.8 0.28 85)" }}
        />
        {/* outer line */}
        <span
          className="pointer-events-none absolute top-5 right-0 bottom-5 w-px"
          style={{ background: "oklch(0.8 0.28 85 / 0.5)" }}
        />
      </div>

      {/* ── Bottom edge ─────────────────────────────────────────────────── */}
      <div className="absolute right-0 bottom-0 left-0 h-[5px]">
        {/* inner line */}
        <span
          className="pointer-events-none absolute top-0 right-3.5 left-3.5 h-px"
          style={{ background: "oklch(0.8 0.28 85)" }}
        />
        {/* outer line */}
        <span
          className="pointer-events-none absolute right-5 bottom-0 left-5 h-px"
          style={{ background: "oklch(0.8 0.28 85 / 0.5)" }}
        />
      </div>
    </div>
  )
}

function Card({ className, children, ...props }: React.ComponentProps<"div">) {
  return (
    <div className="group relative flex flex-col [&>[data-slot=card]]:flex-1">
      <NeonFrame />
      <div
        data-slot="card"
        className={cn(
          "text-card-foreground relative flex flex-col gap-6 overflow-visible border-none bg-[oklch(0.2_0.008_240)] py-6 shadow-[0_2px_8px_oklch(0_0_0/0.6)]",
          className,
        )}
        {...props}
      >
        <Rivets />
        {children}
      </div>
    </div>
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({
  className,
  style,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("font-display text-xl leading-none font-bold", className)}
      style={{
        color: "var(--accent)",
        textShadow:
          "0 0 4px oklch(0.8 0.16 85 / 0.25), 0 0 16px oklch(0.8 0.3 145 / 0.12), 0 1px 0 oklch(0.55 0.1 85), 0 1px 3px oklch(0 0 0 / 0.4)",
        ...style,
      }}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
}
